import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlagsContext } from '@/contexts/FeatureFlagsContext';
import { useStoredRedirect } from '@/components/auth/RouteGuard';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { handlePostLoginActions } from '@/utils/postLoginHandler';
import { useCallback, useRef } from 'react';

export const useAuthController = () => {
  const auth = useAuth();
  const { refreshFlags } = useFeatureFlagsContext();
  const { getStoredRedirect, clearStoredRedirect, setStoredRedirect } = useStoredRedirect();
  const navigate = useNavigate();
  
  // Track the last auth status to prevent unnecessary refreshes
  const lastAuthStatusRef = useRef<string | null>(null);

  const signInWithRedirect = useCallback(async (email: string, password: string) => {
    const { error } = await auth.signIn(email, password);
    
    if (!error) {
      // Only refresh feature flags if the auth status actually changed
      const currentStatus = auth.status;
      if (currentStatus !== lastAuthStatusRef.current) {
        console.log('[useAuthController] Auth status changed, refreshing flags:', {
          from: lastAuthStatusRef.current,
          to: currentStatus
        });
        lastAuthStatusRef.current = currentStatus;
        await refreshFlags();
      }
      
      // Handle post-login actions (redirects, etc.)
      await handlePostLoginActions(navigate);
    }
    
    return { error };
  }, [auth, refreshFlags, navigate]);

  const signUpWithRedirect = useCallback(async (email: string, password: string) => {
    const { error } = await auth.signUp(email, password);
    
    if (!error) {
      // Only refresh feature flags if the auth status actually changed
      const currentStatus = auth.status;
      if (currentStatus !== lastAuthStatusRef.current) {
        console.log('[useAuthController] Auth status changed, refreshing flags:', {
          from: lastAuthStatusRef.current,
          to: currentStatus
        });
        lastAuthStatusRef.current = currentStatus;
        await refreshFlags();
      }
      
      // Handle post-login actions (redirects, etc.)
      await handlePostLoginActions(navigate);
    }
    
    return { error };
  }, [auth, refreshFlags, navigate]);

  const signOutWithCleanup = useCallback(async () => {
    // Clear any stored redirect intent
    clearStoredRedirect();
    
    // Sign out
    const { error } = await auth.signOut();
    
    if (!error) {
      // Only refresh feature flags if the auth status actually changed
      const currentStatus = auth.status;
      if (currentStatus !== lastAuthStatusRef.current) {
        console.log('[useAuthController] Auth status changed, refreshing flags:', {
          from: lastAuthStatusRef.current,
          to: currentStatus
        });
        lastAuthStatusRef.current = currentStatus;
        await refreshFlags();
      }
      
      // Navigate to home
      navigate(ROUTES.HOME);
    }
    
    return { error };
  }, [auth, refreshFlags, navigate, clearStoredRedirect]);

  const refreshSessionAndFlags = useCallback(async () => {
    await auth.refreshSession();
    // Only refresh flags if the session actually changed
    const currentStatus = auth.status;
    if (currentStatus !== lastAuthStatusRef.current) {
      console.log('[useAuthController] Session refreshed, auth status changed:', {
        from: lastAuthStatusRef.current,
        to: currentStatus
      });
      lastAuthStatusRef.current = currentStatus;
      await refreshFlags();
    }
  }, [auth, refreshFlags]);

  return {
    // Auth state
    user: auth.user,
    session: auth.session,
    status: auth.status,
    isLoading: auth.isLoading,
    
    // Auth actions
    signIn: signInWithRedirect,
    signUp: signUpWithRedirect,
    signOut: signOutWithCleanup,
    refreshSession: refreshSessionAndFlags,
    clearError: auth.clearError,
    
    // Redirect management
    getStoredRedirect,
    setStoredRedirect,
    clearStoredRedirect,
    
    // Feature flags
    refreshFlags,
  };
};
